import type { CarouselGenerationRun, CarouselItem, CarouselSourceHit } from "@/lib/api";

export type ConceptsTraceOutgoing = {
  pain: string;
  book: string;
  tool: string;
  sku: string;
  formatId: string;
  styleId: string;
  useBook: boolean;
  useComments: boolean;
  useMessenger: boolean;
  useStories: boolean;
  urls: string;
  note: string;
  painLabel?: string;
  bookLabel?: string;
  toolLabel?: string;
  skuLabel?: string;
  formatLabel?: string;
  styleLabel?: string;
  sources: CarouselSourceHit[];
};

export function packFromItem(
  item?: CarouselItem,
  outgoing?: ConceptsTraceOutgoing | null,
): string {
  const stored = (item?.latest_generation?.pack || "").trim();
  if (stored) return stored;
  const run = item?.latest_generation;
  return formatClientPack(item, outgoing, run);
}

function formatClientPack(
  item?: CarouselItem,
  outgoing?: ConceptsTraceOutgoing | null,
  run?: CarouselGenerationRun | null,
): string {
  const ideas = item?.ideas || run?.ideas || [];
  const sources: CarouselSourceHit[] = item?.sources?.length
    ? item.sources
    : outgoing?.sources || [];
  const lines = [
    "# Przebieg karuzeli (do strojenia)",
    `id: ${item?.id || ""}`,
    `short_id: ${item?.short_id || outgoing?.formatLabel || ""}`,
    `krok: ${run?.step || "concepts"}`,
    `kiedy: ${run?.created_at || ""}`,
    `źródło: ${run?.source || item?.concepts_source || ""}`,
    `model: ${run?.model || "n/d"}`,
    "",
    "## Brief",
    `- ból: ${outgoing?.painLabel || item?.pain_id || "wszystkie"}`,
    `- sku: ${outgoing?.skuLabel || item?.sku || ""}`,
    `- typ: ${outgoing?.formatLabel || item?.format_id || "mieszanka"}`,
    `- styl: ${outgoing?.styleLabel || item?.style_id || ""}`,
    `- książka: ${outgoing?.useBook === false ? "wyłączona" : outgoing?.bookLabel || item?.book_id || "wszystkie"}`,
    `- narzędzie: ${outgoing?.toolLabel || item?.tool_id || "bez filtra"}`,
    `- komentarze: ${outgoing?.useComments === false ? "nie" : "tak"}`,
    `- messenger: ${outgoing?.useMessenger === false ? "nie" : "tak"}`,
    `- historyjki: ${outgoing?.useStories === false ? "nie" : "tak"}`,
    `- notatka: ${outgoing?.note || "brak"}`,
    "",
    `## Źródła wysłane (${sources.length})`,
  ];
  if (!sources.length) lines.push("(pusto)");
  sources.forEach((src, i) => {
    lines.push(`### ${i + 1}. ${src.type || src.kind || "?"} · ${src.meta || src.id || ""}`);
    lines.push(src.excerpt || src.hook || "(brak excerptu)");
    lines.push("");
  });
  lines.push("## Prompt do modelu", "", run?.prompt ? `\`\`\`\n${run.prompt}\n\`\`\`` : "(jeszcze nie zapisany albo szablon)");
  lines.push("", `## Koncepcje (${ideas.length})`, "");
  ideas.forEach((idea, i) => {
    lines.push(`### ${i + 1}. ${idea.hook || "(brak hooka)"}`);
    lines.push(`typ: ${idea.format_id || idea.format_source || "?"}`);
    if (idea.concept) lines.push(`koncepcja: ${idea.concept}`);
    if (idea.effect) lines.push(`efekt: ${idea.effect}`);
    lines.push("");
  });
  lines.push(
    "## Feedback (dopisz tu)",
    "- co zostawić:",
    "- co uciąć / zmienić:",
    "- który kąt był zły i dlaczego:",
    "- jaki prompt / reguła powinna to złapać następnym razem:",
  );
  return lines.join("\n");
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.left = "-9999px";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }
}
